import {KpisList, type PerformanceDescendantWithId} from '@domain/kpis'
import {HierarchyNodeType} from '@domain/shared'
import {type AggregatedKpiData} from '@features/performance-reports'
import {DateRange} from '@hconnect/uikit/src/common'
import {ListTable} from '@hconnect/uikit/src/lib2'
import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useNavigate, useLocation, useParams} from 'react-router'

import {useHierarchyData} from '../hooks/useHierarchy'
import {useTranslationPrefix} from '../hooks/useTranslationPrefix'
import {AREA_KPI, LOCATION_KPI, PLANT_PERFORMANCE} from '../routing'
import {Comment, CommentLocationData} from '../types'

import {generateColumns} from './actualVsPlanned.columns'

const getPath = (path: string, params: Record<string, string | undefined>, search) =>
  generatePath(path, params) + search

type PathParameter = {
  locationId?: string
}

type MobileLocationCardsProps = {
  data: AggregatedKpiData
  hasQuarterlyData: boolean
  currentTab: KpisList
  dateRange: DateRange<Date>
  setCommentLocationData: (id: CommentLocationData) => void
  comments: Comment[]
}

export const MobileLocationCards = ({
  data,
  hasQuarterlyData,
  currentTab,
  dateRange,
  setCommentLocationData,
  comments
}: MobileLocationCardsProps) => {
  const {t, i18n} = useTranslation()
  const {performancePrefix} = useTranslationPrefix()
  const navigate = useNavigate()
  const {search} = useLocation()
  const {locationId} = useParams<PathParameter>()
  const hierarchy = useHierarchyData()

  const onClick = useCallback(
    (item: PerformanceDescendantWithId<AggregatedKpiData>) => {
      if (
        item.locationData?.type === HierarchyNodeType.Region ||
        item.locationData?.type === HierarchyNodeType.Country
      ) {
        void navigate(getPath(LOCATION_KPI, {locationId: item.locationData?.id}, search))
      }
      if (item.locationData?.type === HierarchyNodeType.Area) {
        void navigate(getPath(AREA_KPI, {locationId: item.locationData?.id}, search))
      }
      if (
        (!item.locationData?.type && locationId) ||
        item.locationData?.type === HierarchyNodeType.Plant
      ) {
        void navigate(getPath(PLANT_PERFORMANCE, {plantId: item.locationData?.id}, search))
      }
    },
    [locationId, navigate, search]
  )

  const columns = useMemo(() => {
    const {historicalData} = data
    return generateColumns({
      translationFn: (key, args = {}) => t(`${performancePrefix}.${key}`, args),
      currentTab,
      offsetYearsAmount: historicalData?.length,
      dateRange,
      locale: i18n.language,
      setCommentLocationData,
      comments,
      hierarchy,
      showQuarterlyData: hasQuarterlyData,
      isMobile: true
    })
  }, [
    t,
    currentTab,
    data,
    dateRange,
    i18n.language,
    setCommentLocationData,
    comments,
    hierarchy,
    performancePrefix,
    hasQuarterlyData
  ])

  return (
    <ListTable
      data={data.descendants}
      getRowDataTestId={(item) => `ac-vs-pl-table-row-${item.id}`}
      columns={columns}
      onClick={(item) => onClick(item)}
    />
  )
}
